import { useEffect, useRef, useState } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { useTranslation } from 'react-i18next';
import { useImage } from "../hooks/useImage";

export default function Example() {

  const { t } = useTranslation()
  const container = useRef(null)
  const isInView = useInView(container)
  const animation = useAnimation()
  
  // New state to track if the specific image file has finished loading
  const [isImageReady, setIsImageReady] = useState(false);

  const { imageUrl, error } = useImage('qLKZaB8y8BHcJAwIh9G7');

  useEffect(() => {
    if(isInView) {
      animation.start({
        y: 0,
        opacity: 1,
        transition: {
          type: 'ease',
          duration: 1.5,
          ease: 'easeOut'
        }
      })
    }
  }, [isInView, animation]);

  // Determine which source to use
  const imageSource = imageUrl || (error ? "/assets/img/GrupoACBY.jpg" : null);

  return (
    <div ref={container}>
      <div className="bg-red-600 text-white text-center py-4">
        <p className="text-lg font-bold">
          {t('bannerAnnouncement')}
        </p>
      </div>
      <motion.div className="relative isolate overflow-hidden bg-gray-900"
        initial={{ y: 100, opacity: 0 }}
        animate={animation}
      >
        {/* CONDITIONAL RENDERING + SMOOTH LOAD
            1. We check if 'imageSource' exists.
            2. We use 'motion.img' to animate the opacity.
            3. We set 'animate' based on 'isImageReady'. It stays invisible (0) until loaded, then fades to 0.3.
            4. 'onLoad' triggers the state change once the browser has the full image data.
        */}
        {imageSource && (
          <motion.img
            src={imageSource}
            alt="Animal Care By Yaraliz - Pride Day"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            
            // Start invisible
            initial={{ opacity: 0 }}
            // Animate to 0.3 (your desired opacity) ONLY when ready
            animate={{ opacity: isImageReady ? 0.3 : 0 }}
            transition={{ duration: 0.5 }}
            
            // This function runs when the image data is fully downloaded
            onLoad={() => setIsImageReady(true)}
            
            className="absolute inset-0 -z-10 h-full w-full object-cover object-[center_25%]"
          />
        )}
        
        <div className="absolute inset-x-0 top-[-10rem] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[-20rem]">
          <svg
            className="relative left-[calc(50%-11rem)] -z-10 h-[21.1875rem] max-w-none -translate-x-1/2 rotate-[30deg] sm:left-[calc(50%-30rem)] sm:h-[42.375rem]"
            viewBox="0 0 1155 678"
          >
            <path
              fill="url(#45de2b6b-92d5-4d68-a6a0-9b9b2abad533)"
              fillOpacity=".2"
              d="M317.219 518.975L203.852 678 0 438.341l317.219 80.634 204.172-286.402c1.307 132.337 45.083 346.658 209.733 145.248C936.936 126.058 882.053-94.234 1031.02 41.331c119.18 108.451 130.68 295.337 121.53 375.223L855 299l21.173 362.054-558.954-142.079z"
            />
            <defs>
              <linearGradient
                id="45de2b6b-92d5-4d68-a6a0-9b9b2abad533"
                x1="1155.49"
                x2="-78.208"
                y1=".177"
                y2="474.645"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#FF9693" />
                <stop offset={1} stopColor="#FF9693" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className="px-6 lg:px-8">
          <div className="mx-auto max-w-2xl py-64 sm:py-80 lg:py-[28rem]">
            <div className="text-center">
              <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
                {t('animalCareByYaraliz')}
              </h1>
              <p className="mt-6 text-lg leading-8 text-gray-300">
                {t('homeHeaderDescription')}
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <a
                  href="https://acby.portal.gingrapp.com/"
                  className="rounded-md bg-custom-light-orange px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-custom-rose focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
                >
                  {t('contactUs')}
                </a>
                <a href="/services" className="text-sm font-semibold leading-6 text-white">
                  {t('services')} <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]">
          <svg
            className="relative left-[calc(50%+3rem)] h-[21.1875rem] max-w-none -translate-x-1/2 sm:left-[calc(50%+36rem)] sm:h-[42.375rem]"
            viewBox="0 0 1155 678"
          >
            <path
              fill="url(#ecb5b0c9-546c-4772-8c71-4d3f06d544bc)"
              fillOpacity=".2"
              d="M317.219 518.975L203.852 678 0 438.341l317.219 80.634 204.172-286.402c1.307 132.337 45.083 346.658 209.733 145.248C936.936 126.058 882.053-94.234 1031.02 41.331c119.18 108.451 130.68 295.337 121.53 375.223L855 299l21.173 362.054-558.954-142.079z"
            />
            <defs>
              <linearGradient
                id="ecb5b0c9-546c-4772-8c71-4d3f06d544bc"
                x1="1155.49"
                x2="-78.208"
                y1=".177"
                y2="474.645"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#FFB5A5" />
                <stop offset={1} stopColor="#FFB5A5" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </motion.div>
    </div>
  )
}